import NetInfo, { NetInfoState, NetInfoSubscription } from '@react-native-community/netinfo';
import { Platform } from 'react-native';
import { NetworkLogger } from '../utils/logger';
import { networkActions } from '../state/globalState';
import { APP_CONSTANTS } from '../config/constants';
import { getSupabasePublicApiKey, getSupabaseUrl } from './supabaseEnv';

// Logger específico para o serviço de rede
const logger = NetworkLogger.Main;
const statusLogger = NetworkLogger.Status;
const eventsLogger = NetworkLogger.Events;

// Variável para armazenar a assinatura do listener de rede (mobile)
let netInfoUnsubscribe: NetInfoSubscription | null = null;

// Variável para armazenar os listeners de rede (web)
let onlineListener: (() => void) | null = null;
let offlineListener: (() => void) | null = null;

const WEB_SUPABASE_HEALTHCHECK_TIMEOUT_MS = Math.min(
  APP_CONSTANTS.TIMEOUTS.NETWORK_TIMEOUT,
  5000
);

const getSupabaseHealthcheckUrl = (): string | null => {
  const baseUrl = getSupabaseUrl();
  if (!baseUrl) {
    return null;
  }

  const healthUrl = new URL(`${baseUrl.replace(/\/+$/, '')}/auth/v1/health`);
  const { key: publicApiKey } = getSupabasePublicApiKey();

  // Supabase exige apikey para os endpoints protegidos pelo gateway.
  // Em navegador, usar query param evita preflight e remove ruído de 401 no DevTools.
  if (publicApiKey) {
    healthUrl.searchParams.set('apikey', publicApiKey);
  } else {
    statusLogger.warn('[Web] Chave pública do Supabase não encontrada para healthcheck ativo.');
  }

  return healthUrl.toString();
};

/**
 * Em navegadores móveis, navigator.onLine pode retornar true mesmo sem rota útil até o backend.
 * Este healthcheck considera conectividade válida quando conseguimos obter qualquer resposta HTTP
 * do host do Supabase (200/401/404 ainda são válidos para alcance de rede).
 */
const checkWebSupabaseReachability = async (): Promise<boolean> => {
  const healthcheckUrl = getSupabaseHealthcheckUrl();
  if (!healthcheckUrl) {
    statusLogger.warn('[Web] EXPO_PUBLIC_SUPABASE_URL ausente. Pulando healthcheck ativo de conectividade.');
    return true;
  }

  const startedAt = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), WEB_SUPABASE_HEALTHCHECK_TIMEOUT_MS);

  try {
    const response = await fetch(healthcheckUrl, {
      method: 'GET',
      signal: controller.signal,
    });

    const elapsed = Date.now() - startedAt;
    statusLogger.debug(`[Web] Healthcheck Supabase respondeu em ${elapsed}ms (status=${response.status})`);
    return true;
  } catch (error) {
    const elapsed = Date.now() - startedAt;
    statusLogger.warn(`[Web] Healthcheck Supabase falhou em ${elapsed}ms`, error);
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Atualiza o estado de conexão de rede
 * @param isConnected Status de conexão
 * @param connectionType Tipo de conexão (cellular, wifi, etc.)
 */
const updateConnectionState = (isConnected: boolean, connectionType: string | null = null) => {
  statusLogger.debug(`Atualizando estado de conexão: ${isConnected ? 'conectado' : 'desconectado'}${connectionType ? ` (${connectionType})` : ''}`);
  
  // Atualizar estado global
  networkActions.setConnectionState(isConnected, connectionType);
};

/**
 * Verifica o estado atual da conexão
 * @returns Promise com o estado atual da conexão
 */
export const checkConnection = async (): Promise<boolean> => {
  try {
    let isConnected = false;
    let connectionType = null;
    
    if (Platform.OS === 'web') {
      // No web, usamos navigator.onLine
      isConnected = typeof navigator !== 'undefined' && navigator.onLine;
      connectionType = isConnected ? 'web' : null;

      if (isConnected) {
        const reachable = await checkWebSupabaseReachability();
        if (!reachable) {
          statusLogger.warn('[Web] navigator.onLine=true, mas backend Supabase está inalcançável. Marcando como desconectado.');
          isConnected = false;
          connectionType = null;
        }
      }

      statusLogger.debug(
        `[Web] Verificação de conexão efetiva: ${isConnected ? 'conectado' : 'desconectado'}`
      );
    } else {
      // No mobile, usamos NetInfo
      const state = await NetInfo.fetch();
      isConnected = !!state.isConnected;
      connectionType = state.type;
      
      statusLogger.debug(`[Mobile] checkConnection(): ${isConnected ? 'conectado' : 'desconectado'} (${state.type})`);
    }
    
    // Atualizar estado
    updateConnectionState(isConnected, connectionType);
    return isConnected;
  } catch (error) {
    logger.error('Erro ao verificar conexão:', error);
    return false;
  }
};

/**
 * Inicializa o monitoramento de rede
 */
export const initNetworkMonitoring = async (): Promise<void> => {
  //logger.info('Inicializando monitoramento de rede');
  
  try {
    // Verificação inicial de conexão
    await checkConnection();
    
    if (Platform.OS === 'web') {
      // Configurar listeners para web
      eventsLogger.debug('Configurando listeners de rede para web');
      
      // Remover listeners anteriores se existirem
      if (onlineListener) {
        window.removeEventListener('online', onlineListener);
      }
      if (offlineListener) {
        window.removeEventListener('offline', offlineListener);
      }
      
      // Criar novos listeners
      onlineListener = () => {
        eventsLogger.info('Evento online: Dispositivo conectado à rede');
        updateConnectionState(true, 'web');
      };
      
      offlineListener = () => {
        eventsLogger.info('Evento offline: Dispositivo desconectado da rede');
        updateConnectionState(false, null);
      };
      
      // Adicionar listeners
      window.addEventListener('online', onlineListener);
      window.addEventListener('offline', offlineListener);
      
      logger.info('Monitoramento de rede iniciado para web');
    } else {
      // Configurar NetInfo para mobile
      eventsLogger.debug('Configurando NetInfo para mobile');
      
      // Remover assinatura anterior se existir
      if (netInfoUnsubscribe) {
        netInfoUnsubscribe();
      }
      
      // Criar nova assinatura
      netInfoUnsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
        const isConnected = !!state.isConnected;
        const connectionType = state.type;
        
        eventsLogger.info(`Evento de alteração de rede: ${isConnected ? 'conectado' : 'desconectado'} (${connectionType})`);
        updateConnectionState(isConnected, connectionType);
      });
      
      logger.info('Monitoramento de rede iniciado para mobile com SUCESSO');
    }
  } catch (error) {
    logger.error('Erro ao inicializar monitoramento de rede:', error);
  }
};

/**
 * Remove o monitoramento de rede
 */
export const stopNetworkMonitoring = (): void => {
  logger.info('Parando monitoramento de rede');
  
  try {
    if (Platform.OS === 'web') {
      // Remover listeners web
      if (onlineListener) {
        window.removeEventListener('online', onlineListener);
        onlineListener = null;
      }
      if (offlineListener) {
        window.removeEventListener('offline', offlineListener);
        offlineListener = null;
      }
    } else {
      // Remover assinatura do NetInfo
      if (netInfoUnsubscribe) {
        netInfoUnsubscribe();
        netInfoUnsubscribe = null;
      }
    }
    
    logger.debug('Monitoramento de rede parado com sucesso');
  } catch (error) {
    logger.error('Erro ao parar monitoramento de rede:', error);
  }
};

// Exportar funções
export default {
  initNetworkMonitoring,
  checkConnection,
  stopNetworkMonitoring,
}; 
